import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GraduationCap, Languages, LogOut, Menu } from "lucide-react";
import { useState } from "react";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const goToSection = (id, onClick) => {
    if (onClick) onClick();
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const linkCls = "text-sm font-semibold text-foreground/80 hover:text-primary transition-colors";

  const NavLinks = ({ onClick }) => (
    <>
      <Link data-testid="nav-home" to="/" onClick={onClick} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors">
        {t("nav_home")}
      </Link>
      <Link data-testid="nav-knowledge" to="/knowledgebase" onClick={onClick} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors">
        {t("nav_knowledge")}
      </Link>
      <button data-testid="nav-chat" onClick={() => goToSection("chat", onClick)} className={linkCls}>
        {t("nav_chat")}
      </button>
      <button data-testid="nav-tutor" onClick={() => goToSection("tutor", onClick)} className={linkCls}>
        {t("nav_tutor")}
      </button>
      {user && user.role === "parent" && (
        <Link data-testid="nav-dashboard" to="/dashboard" onClick={onClick} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors">
          {t("nav_dashboard")}
        </Link>
      )}
      {user && user.role === "principal" && (
        <Link data-testid="nav-billing" to="/billing" onClick={onClick} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors">
          {t("nav_billing")}
        </Link>
      )}
      {user && user.role === "admin" && (
        <Link data-testid="nav-admin" to="/admin" onClick={onClick} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors">
          {t("nav_admin")}
        </Link>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link data-testid="brand-link" to="/" className="flex items-center gap-2.5">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
            <GraduationCap className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight text-foreground">{t("brand")}</span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          <NavLinks />
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-semibold text-accent hover:border-primary hover:text-primary transition-colors"
          >
            <Languages className="h-4 w-4" strokeWidth={1.75} />
            {t("lang_label")}
          </button>

          {user ? (
            <div className="hidden items-center gap-2 md:flex">
              <span className="text-sm font-medium text-muted-foreground">{user.name?.split(" ")[0]}</span>
              <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="rounded-full">
                <LogOut className="h-4 w-4" strokeWidth={1.75} />
              </Button>
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Button data-testid="header-login-btn" asChild variant="ghost" className="rounded-full font-semibold">
                <Link to="/login">{t("nav_login")}</Link>
              </Button>
              <Button data-testid="header-signup-btn" asChild className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover">
                <Link to="/register">{t("nav_signup")}</Link>
              </Button>
            </div>
          )}

          <button data-testid="mobile-menu-btn" className="md:hidden" onClick={() => setOpen((o) => !o)}>
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="flex flex-col gap-4 border-t border-border bg-card px-5 py-5 md:hidden">
          <NavLinks onClick={() => setOpen(false)} />
          {user ? (
            <Button data-testid="mobile-logout-btn" onClick={handleLogout} variant="outline" className="rounded-full">{t("nav_logout")}</Button>
          ) : (
            <div className="flex gap-2">
              <Button asChild variant="outline" className="flex-1 rounded-full"><Link to="/login" onClick={() => setOpen(false)}>{t("nav_login")}</Link></Button>
              <Button asChild className="flex-1 rounded-full bg-primary hover:bg-primary-hover"><Link to="/register" onClick={() => setOpen(false)}>{t("nav_signup")}</Link></Button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
